// Delegated handlers (data-dws-on-<event>="name") instead of inline event handlers, the picker key handling they
// run, focus return (DwsAlert, DwsBanner, DwsToast, DwsSnackbar), raising self-raising layers into the top layer,
// and DwsErrorBoundary's focus tracking. Core: the markup relies on these document listeners.

import { publish } from "../shared/publish";
import { showPopupLayer } from "../shared/popupLayer";

// Blocks all typing on a native <input> except Tab (used by DwsDatePicker's
// OnlyDialog behavior). Blazor's @onkeydown:preventDefault directive only supports a
// static per-render bool, not a per-keystroke condition, so this is wired via a plain
// onkeydown="" attribute referencing this named function rather than the anonymous
// inline JS strings the component used to build in C#.
// OnlyDialog pickers block typing, but the keyboard still needs a way to the dialog that a click opens: Enter, Space,
// F4 and (Alt+)ArrowDown open the native picker. A readonly input (permission register mode) refuses showPicker.
function DwsPickerOpenFromKey(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement;
    const opens = event.key === 'Enter' || event.key === ' ' || event.key === 'F4' || event.key === 'ArrowDown';

    if (!opens || !input || input.readOnly || input.disabled || typeof input.showPicker !== 'function') {
        return false;
    }

    try {
        input.showPicker();
        return true;
    } catch (e) {
        return false;
    }
}

function DwsDatePickerAllowOnlyTabKey(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
        event.preventDefault();
        DwsPickerOpenFromKey(event);
    }
}

// Keyboard handling of DwsDateTimeRangePicker's two fields (DwsDateTimePicker text fields), wired through a delegated
// handler (data-dws-on-keydown) on each field's frame for the same reason as above. Enter in the start field moves to
// the end field, Shift+Enter in the end field back to the start.
function DwsDateTimeRangePickerKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const field = input && input.dataset ? input.dataset.dwsRangeField : null;

    if (event.key === 'Enter' && !event.altKey && !event.ctrlKey && !event.metaKey) {
        const target = field === 'start' && !event.shiftKey ? 'end'
            : field === 'end' && event.shiftKey ? 'start'
            : null;
        const root = input.closest('.dws-datetime-range-picker');
        const next = target && root ? root.querySelector<HTMLElement>('[data-dws-range-field="' + target + '"]') : null;

        if (next) {
            // Also keeps Enter from submitting a surrounding form while moving between the fields.
            event.preventDefault();
            next.focus();
            return;
        }
    }
}

// Raises an element that raises itself on its animationstart (DwsBanner, DwsBannerStack, a full-screen DwsOverlay) into
// the top layer: the backup for a page whose CSS animations do not run (a background tab, a hidden embedding view). The
// layer's delegated animationstart handler has usually raised it already, in the frame after it was inserted. (It used
// to call DwsCalendarShowPopupLayer, which did the same; both now share shared/popupLayer.ts.)
function DwsShowPopoverLayer(element: Element | null): void {
    showPopupLayer(element as HTMLElement | null);
}

// Hands the focus back when a layer that holds it goes away (DwsBanner closed with its button, hidden by its timer or
// from code): the focus fell to <body>, and a keyboard user had to start again from the top of the page. The layer
// is marked data-dws-on-focusin="return-focus" and the delegated handler of that name records where the focus came
// from (element._dwsReturnFocus); without one the focus stays.
function DwsReturnFocusFrom(element: Element | null): boolean {
    if (!element) {
        return false;
    }

    const active = document.activeElement;
    if (!active || !element.contains(active)) {
        return false;
    }

    const target = element._dwsReturnFocus as HTMLButtonElement | undefined;
    if (!target || !target.isConnected || target.disabled || element.contains(target) || typeof target.focus !== "function") {
        return false;
    }

    target.focus();
    return document.activeElement === target;
}

// Delegated handlers (fix batch 48). The library's markup carries no inline event handlers (onkeydown="...",
// onanimationstart="..."): a page with a Content-Security-Policy would need 'unsafe-inline' for them. An element
// names its handler instead - data-dws-on-keydown="calendar-keydown", optionally with one argument after a colon
// (data-dws-on-keydown="datetime-input:true") - and one capturing listener per event type on the document runs it,
// for the event's target and every marked element around it, innermost first, with the element as "this" (what an
// inline handler had). Only the names in this table run: the attribute cannot call anything else. The handlers do
// what Blazor cannot: cancel a key or a drag depending on the event, or act in the very frame an element appears.
// One handler per line - the tests read a handler's text from here by its name.
// The date-time picker's key handlers run inside the key event (they cancel it), so they come from shared code the
// core bundles, not through the pickers module's globals (a stand-in would run them a moment too late).
import { DwsDateTimePickerInputKeyDown, DwsDateTimePickerNavKeyDown } from "../shared/dateTimePickerKeys";

const DwsDelegatedHandlers = {
    // DwsAlert, DwsBanner, DwsToast, DwsSnackbar: where the focus came from, for DwsReturnFocusFrom.
    "return-focus": function (event) { if (event.relatedTarget && !this.contains(event.relatedTarget)) { this._dwsReturnFocus = event.relatedTarget; } },
    // A layer that raises itself into the top layer on its own animationstart (DwsBanner, DwsBannerStack, DwsOverlay).
    "raise-self": function (event) { if (this.showPopover && event.target === this) { try { if (!this.matches(':popover-open')) { this.showPopover(); } } catch (e) { } } },
    // The same for any animation that starts inside the layer (DwsCalendar's popup layer, DwsScheduler's prompt layer).
    "raise": function (event) { if (this.showPopover) { try { if (!this.matches(':popover-open')) { this.showPopover(); } } catch (e) { } } },
    // DwsBarcodeScanner: opens the file picker inside the user's own click (the argument is the file input's id).
    "click-element": function (event, argument) { var target = argument ? document.getElementById(argument) : null; if (target) { target.click(); } },
    // DwsSelect / DwsNativeSelect in permission register mode: the native select takes the focus but no change.
    "cancel": function (event) { event.preventDefault(); },
    "cancel-unless-tab": function (event) { if (event.key !== 'Tab') { event.preventDefault(); } },
    // The pickers' key handling (see the functions).
    "date-only-tab": function (event) { DwsDatePickerAllowOnlyTabKey(event); },
    "datetime-input": function (event, argument) { DwsDateTimePickerInputKeyDown(event, argument === "true"); },
    "datetime-nav": function (event) { DwsDateTimePickerNavKeyDown(event); },
    "datetime-range": function (event) { DwsDateTimeRangePickerKeyDown(event); },
    // DwsCalendar's root and body, DwsScheduler's prompt layer.
    "calendar-dragstart": function (event) { this.dwsOwnDrag = true; if (event.dataTransfer) { if (event.target.closest) { var source = event.target.closest('[draggable=true]'); if (source) { event.dataTransfer.setData('text/plain', ''); event.dataTransfer.effectAllowed = 'move'; var span = Number(source.getAttribute('data-span')); if (span) { var box = source.getBoundingClientRect(); if (box.width) { event.dataTransfer.setData('application/x-dws-col-' + Math.floor((event.clientX - box.left) * span / box.width), ''); } } } } } },
    "calendar-dragover": function (event) { if (this.classList.contains('dws-calendar--dragging')) { event.preventDefault(); } else if (this.hasAttribute('data-dws-external-drop')) { if (!this.dwsOwnDrag) { var cell = event.target.closest ? event.target.closest('.dws-calendar__slot, .dws-calendar__day-cell') : null; if (cell) { if (/--blocked/.test(cell.className)) { cell = null; } } this.setAttribute('data-dws-external-drag', ''); if (this.dwsExternalTarget !== cell) { if (this.dwsExternalTarget) { this.dwsExternalTarget.removeAttribute('data-dws-external-target'); } this.dwsExternalTarget = cell; if (cell) { cell.setAttribute('data-dws-external-target', ''); } } if (cell) { event.preventDefault(); } } } },
    "calendar-dragleave": function (event) { if (this.hasAttribute('data-dws-external-drag')) { if (!this.contains(event.relatedTarget)) { this.removeAttribute('data-dws-external-drag'); if (this.dwsExternalTarget) { this.dwsExternalTarget.removeAttribute('data-dws-external-target'); this.dwsExternalTarget = null; } } } },
    "calendar-dragend": function (event) { this.dwsOwnDrag = false; },
    "calendar-keydown": function (event) { if (!event.altKey) { if (!event.ctrlKey) { if (!event.metaKey) { if (!event.shiftKey) { if (/^(Home|End|ArrowUp|ArrowDown|PageUp|PageDown)$/.test(event.key)) { if (event.target.classList) { if (event.target.classList.contains('dws-calendar__appointment')) { event.preventDefault(); if (event.key.indexOf('Page') !== 0) { var pieces = Array.prototype.filter.call(event.target.parentElement.children, function (piece) { return piece.classList.contains('dws-calendar__appointment'); }); pieces.sort(function (a, b) { var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect(); return (ra.top - rb.top) || (ra.left - rb.left); }); var at = pieces.indexOf(event.target); var goal = event.key === 'Home' ? pieces[0] : event.key === 'End' ? pieces[pieces.length - 1] : pieces[at + (event.key === 'ArrowDown' ? 1 : -1)]; if (goal) { goal.focus(); } } } } } } } } }  if (event.key === ' ') { if (event.target.classList) { if (event.target.classList.contains('dws-calendar__appointment')) { event.preventDefault(); } } } else if (event.key === 'Tab') { var popup = event.target.closest ? event.target.closest('.dws-calendar__popup') : null; if (popup) { var items = Array.prototype.filter.call(popup.querySelectorAll('button, input, select, textarea, a[href], [tabindex]'), function (el) { return el.disabled || el.tabIndex === -1 ? false : el.getClientRects().length > 0; }); if (items.length) { var first = items[0], last = items[items.length - 1]; if (event.shiftKey) { if (event.target === first || event.target === popup) { last.focus(); event.preventDefault(); } } else if (event.target === last) { first.focus(); event.preventDefault(); } } } } else if (event.altKey) { if (String(event.key).indexOf('Arrow') === 0) { var appointment = event.target.closest ? event.target.closest('.dws-calendar__appointment') : null; if (appointment) { if (appointment.getAttribute('aria-keyshortcuts')) { event.preventDefault(); } } } } },
    "calendar-body-drop": function (event) { var root = this.parentElement; if (root) { root.dwsOwnDrag = false; root.removeAttribute('data-dws-external-drag'); if (root.dwsExternalTarget) { root.dwsExternalTarget.removeAttribute('data-dws-external-target'); root.dwsExternalTarget = null; } } },
    "scheduler-prompt-keydown": function (event) { if (event.key === 'Tab') { var dialog = this.querySelector('.dws-scheduler__prompt'); var items = Array.prototype.filter.call(this.querySelectorAll('.dws-scheduler__prompt button'), function (el) { return !el.disabled; }); if (items.length) { var first = items[0], last = items[items.length - 1]; if (event.shiftKey) { if (event.target === first || event.target === dialog) { last.focus(); event.preventDefault(); } } else if (event.target === last) { first.focus(); event.preventDefault(); } } } }
};

function DwsRunDelegatedHandlers(type: string, event: Event): void {
    const attribute = "data-dws-on-" + type;
    const selector = "[" + attribute + "]";
    let element = event.target instanceof Element ? event.target.closest(selector) : null;

    while (element) {
        const value = element.getAttribute(attribute) || "";
        const split = value.indexOf(":");
        const name = split < 0 ? value : value.substring(0, split);
        // Looked up before the handler runs: it may take the element out of the page.
        const next = element.parentElement ? element.parentElement.closest(selector) : null;

        if (Object.prototype.hasOwnProperty.call(DwsDelegatedHandlers, name)) {
            DwsDelegatedHandlers[name].call(element, event, split < 0 ? null : value.substring(split + 1));
        }

        element = next;
    }
}

["animationstart", "click", "dragend", "dragleave", "dragover", "dragstart", "drop", "focusin", "keydown", "keypress", "keyup", "mousedown"].forEach(function (type) {
    document.addEventListener(type, function (event) { DwsRunDelegatedHandlers(type, event); }, true);
});

// DwsErrorBoundary: when the content that held the focus was replaced by the error message, the focus fell to <body>
// and a keyboard user had to start again from the top of the page - right after the action that failed. The boundary
// is marked data-dws-focus-track, and one pair of document listeners records whether the focus is inside
// (element._dwsHadFocus; a removed element fires no focusout, so the flag survives the removal). No inline handlers:
// they would need 'unsafe-inline' under a Content-Security-Policy. Then the focus goes to the element marked data-dws-error-focus (a
// template's choice) or to the built-in message. Focus that is somewhere else on the page is left alone.
document.addEventListener("focusin", function (event) {
    let tracked = event.target instanceof Element ? event.target.closest("[data-dws-focus-track]") : null;

    // Boundaries nest (a grid inside a page): each one the focus is in records it.
    while (tracked) {
        tracked._dwsHadFocus = true;
        tracked = tracked.parentElement ? tracked.parentElement.closest("[data-dws-focus-track]") : null;
    }
}, true);

// Focus that moved to another element clears the mark. Without a relatedTarget the focus either left the page's
// elements altogether or its element was removed (some browsers report that as a focusout): looked at a moment later,
// only focus that really sits elsewhere clears it.
document.addEventListener("focusout", function (event) {
    let tracked = event.target instanceof Element ? event.target.closest("[data-dws-focus-track]") : null;
    const related = event.relatedTarget as Node | null;

    while (tracked) {
        const element = tracked;

        if (related) {
            if (!element.contains(related)) {
                element._dwsHadFocus = false;
            }
        } else {
            setTimeout(function () {
                const active = document.activeElement;
                if (active && active !== document.body && !element.contains(active)) {
                    element._dwsHadFocus = false;
                }
            }, 0);
        }

        tracked = element.parentElement ? element.parentElement.closest("[data-dws-focus-track]") : null;
    }
}, true);

function DwsFocusErrorBoundaryMessage(element: Element | null): boolean {
    if (!element || !element._dwsHadFocus) {
        return false;
    }

    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement && !element.contains(active)) {
        return false;
    }

    const target = element.querySelector<HTMLElement>("[data-dws-error-focus]") || element.querySelector<HTMLElement>(".dws-error-boundary-message");
    if (!target || typeof target.focus !== "function") {
        return false;
    }

    if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
    }

    target.focus();
    return document.activeElement === target;
}

// data-dws-notification-shortcut="alt+shift+n" on a notification container (DwsToast / DwsSnackbar FocusShortcut):
// the combination moves the focus to the first control of the newest notification - or to the container, when the
// notification has no control - and, pressed again while the focus is inside, hands it back to where it came from
// (the delegated focusin handler recorded it, see DwsReturnFocusFrom). Nothing happens while no notification is
// shown, so the keys stay with the page then. Same combo format as data-dws-prevent-keys. One delegated listener.

document.addEventListener('keydown', function (e) {
    if (e.defaultPrevented || e.repeat || typeof e.key !== 'string') {
        return;
    }

    const regions = document.querySelectorAll<HTMLElement>('[data-dws-notification-shortcut]');

    if (regions.length === 0) {
        return;
    }

    let parts = [];
    if (e.ctrlKey) parts.push('ctrl');
    if (e.altKey) parts.push('alt');
    if (e.shiftKey) parts.push('shift');
    if (e.metaKey) parts.push('meta');
    parts.push(e.key === ' ' ? 'space' : e.key.toLowerCase());

    let combo = parts.join('+');
    // With Alt held, macOS reports the character the key produces (Alt+N arrives as a dead key, not as "n"), so the
    // physical key is compared as well.
    let codeKey = typeof e.code === 'string' && /^Key[A-Z]$/.test(e.code) ? e.code.charAt(3).toLowerCase()
        : (typeof e.code === 'string' && /^Digit[0-9]$/.test(e.code) ? e.code.charAt(5) : null);
    let codeCombo = codeKey ? parts.slice(0, parts.length - 1).concat([codeKey]).join('+') : null;

    for (let i = 0; i < regions.length; i++) {
        let region = regions[i];
        let wanted = region.getAttribute('data-dws-notification-shortcut');

        if (wanted !== combo && wanted !== codeCombo) {
            continue;
        }

        let cards = region.querySelectorAll('.dws-toast, .dws-snackbar');

        if (cards.length === 0) {
            continue;
        }

        e.preventDefault();

        if (region.contains(document.activeElement)) {
            const focused = document.activeElement as HTMLElement | null;

            if (!DwsReturnFocusFrom(region) && focused && focused.blur) {
                focused.blur();
            }

            return;
        }

        let newest = cards[cards.length - 1];
        const control = newest.querySelector<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');

        (control || region).focus();
        return;
    }
});

publish("DwsShowPopoverLayer", DwsShowPopoverLayer);
publish("DwsReturnFocusFrom", DwsReturnFocusFrom);
publish("DwsFocusErrorBoundaryMessage", DwsFocusErrorBoundaryMessage);
